import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { type CSSProperties } from 'styled-components';

import { Link } from '@sorare/routing';

import { CardPackCategory } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';

import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import {
  Desktop,
  Grid,
  Header,
  Logo,
  MainPack,
  NeonEdge,
  PackImg,
  PacksWrapper,
  SecondaryPack,
  SecondaryPackWrapper,
  SeriesCardPackGroupWrapper,
  StyledBezel,
} from './ui';
import { PackWithBuyButton } from '../PackWithBuyButton';
import type { SeriesCardPackGroup_cardPackGroup } from './__generated__/index.graphql';
import bundesligaPackGroup from './assets/bundesliga-pack-group.webp';
import laligaPackGroup from './assets/laliga-pack-group.webp';
import ligue1PackGroup from './assets/ligue1-pack-group.webp';
import premierleaguePackGroup from './assets/premierleague-pack-group.webp';

const getBgSeriesImage = (cardPackGroupId: string) => {
  if (!cardPackGroupId) return null;
  if (cardPackGroupId.includes('premier-league')) return premierleaguePackGroup;
  if (cardPackGroupId.includes('bundesliga')) return bundesligaPackGroup;
  if (cardPackGroupId.includes('laliga')) return laligaPackGroup;
  if (cardPackGroupId.includes('ligue-1')) return ligue1PackGroup;
  return null;
};

type Pack = SeriesCardPackGroup_cardPackGroup['cardPacks'][number];

type Result = {
  [key in CardPackCategory]?: Pack | null;
};

type Props = {
  cardPackGroup: SeriesCardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
  wide?: boolean;
};

export const SeriesCardPackGroup = ({
  cardPackGroup,
  onClickBuy,
  wide,
}: Props) => {
  const isLaptopOrAbove = useIsLaptop();
  const cardPacksByCategory = cardPackGroup.cardPacks.reduce<Result>(
    (acc, pack) => {
      const { category } = pack;
      if (category) acc[category] = pack;
      return acc;
    },
    {}
  );
  const goldPack = cardPacksByCategory[CardPackCategory.GOLD];
  const silverPack = cardPacksByCategory[CardPackCategory.SILVER];
  const bronzePack = cardPacksByCategory[CardPackCategory.BRONZE];

  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroup.id),
  });
  const { generateSportContextPath } = useSportContext();
  const isSeries = isSpecialCardSet(cardPackGroup?.cardSet?.slug);

  if (!isSeries) return null;

  const bgSeriesImage = getBgSeriesImage(idFromObject(cardPackGroup.id));

  const backgroundImage = bgSeriesImage || bgImage;

  const color = cardPackGroup.cardPacks.find(
    pack => pack.primaryColor
  )?.primaryColor;

  return (
    <ConditionalWrapper
      wrap={!!isLaptopOrAbove}
      Wrapper={StyledBezel}
      props={{
        style: {
          '--box-hover-shadow': `0 0 12px 0 ${color} inset`,
        } as CSSProperties,
        bezelSize: 24,
        radius: 16,
        background: `linear-gradient(
            180deg,
            rgba(14, 14, 14, 0.5) 0%,
            rgba(14, 14, 14, 0.9) 79.85%,
            #0e0e0e 99.69%
          ),
          url(${backgroundImage}) top left / cover no-repeat`,
      }}
    >
      <SeriesCardPackGroupWrapper
        as={LinkBox}
        style={
          {
            '--background-image': `url(${backgroundImage})`,
          } as CSSProperties
        }
        className={classNames({ wide })}
      >
        <NeonEdge side="top" showNeon={'hover'} color={color || ''}>
          <SecondaryPackWrapper>
            <Header>
              <LinkOverlay
                as={isLaptopOrAbove ? Link : 'div'}
                to={`${generateSportContextPath(
                  ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
                  {
                    params: {
                      group: idFromObject(cardPackGroup.id),
                    },
                  }
                )}?pack=${goldPack?.slug || silverPack?.slug || bronzePack?.slug}`}
              >
                {!!cardPackGroup?.pictureUrl && (
                  <Logo
                    src={cardPackGroup.pictureUrl}
                    alt={cardPackGroup.title}
                  />
                )}
              </LinkOverlay>
            </Header>
            <Grid>
              {!!goldPack && (
                <MainPack>
                  <Vertical gap={0.5}>
                    <BrandM>
                      <FormattedMessage
                        id="SeriesCardPackGroup.title"
                        defaultMessage="Star Pack"
                      />
                    </BrandM>
                    <LabelM>
                      <FormattedMessage
                        id="SeriesCardPackGroup.description"
                        defaultMessage="One Guaranteed Star Player!"
                      />
                    </LabelM>
                  </Vertical>
                  <PackWithBuyButton
                    cardPack={goldPack}
                    cardPackGroupId={cardPackGroup.id}
                    onClickBuy={onClickBuy}
                    withEligiblePlayers
                    gap={3}
                    isPremiumPack
                  />
                </MainPack>
              )}
              {!!bronzePack && (
                <SecondaryPack>
                  <PackWithBuyButton
                    cardPack={bronzePack}
                    cardPackGroupId={cardPackGroup.id}
                    onClickBuy={onClickBuy}
                    gap={3}
                  />
                </SecondaryPack>
              )}
              {!!silverPack && (
                <SecondaryPack>
                  <PackWithBuyButton
                    cardPack={silverPack}
                    cardPackGroupId={cardPackGroup.id}
                    onClickBuy={onClickBuy}
                    gap={3}
                  />
                </SecondaryPack>
              )}
            </Grid>
            <Desktop>
              <PacksWrapper>
                {!!silverPack && (
                  <PackImg
                    pictureUrl={proxyUrl(silverPack?.pictureUrl, {
                      cropWidth: 160,
                    })}
                    alt="star"
                  />
                )}
                {!!goldPack && (
                  <PackImg
                    pictureUrl={proxyUrl(goldPack?.pictureUrl, {
                      cropWidth: 160,
                    })}
                    alt="icon"
                  />
                )}
                {!!bronzePack && (
                  <PackImg
                    pictureUrl={proxyUrl(bronzePack?.pictureUrl, {
                      cropWidth: 160,
                    })}
                    alt="basic"
                  />
                )}
              </PacksWrapper>
            </Desktop>
          </SecondaryPackWrapper>
        </NeonEdge>
      </SeriesCardPackGroupWrapper>
    </ConditionalWrapper>
  );
};

SeriesCardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment SeriesCardPackGroup_cardPackGroup on CardPackGroup {
      id
      title
      pictureUrl
      cardSet {
        slug
      }
      cardPacks {
        primaryColor: color(variant: PRIMARY)
        slug
        category
        eligibleCommonPlayers {
          id
          pictureUrl
        }
        ...PackWithBuyButton_cardPack
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<SeriesCardPackGroup_cardPackGroup>,
};
